import { MEDIA, mmToDots, ptToDots } from './media.js';
/** Size of the drawing surface for a layout, in dots, and whether it needs rotating for the printer. */
export function badgeSurface(layout) {
    const spec = MEDIA[layout.media];
    const across = spec.printableDots.width;
    const along = spec.printableDots.length;
    const naturalLandscape = across >= along;
    const wantLandscape = layout.orientation === 'landscape';
    return naturalLandscape === wantLandscape
        ? { widthDots: across, heightDots: along, rotate: 0 }
        : { widthDots: along, heightDots: across, rotate: 90 };
}
function clip(r, maxW, maxH) {
    const x0 = Math.max(0, r.x);
    const y0 = Math.max(0, r.y);
    const x1 = Math.min(maxW, r.x + r.w);
    const y1 = Math.min(maxH, r.y + r.h);
    return x1 - x0 >= 1 && y1 - y0 >= 1 ? { x: x0, y: y0, w: x1 - x0, h: y1 - y0 } : null;
}
function rectOf(el) {
    return { x: mmToDots(el.x), y: mmToDots(el.y), w: Math.max(1, mmToDots(el.w)), h: Math.max(1, mmToDots(el.h)) };
}
/** Ids of elements that fall (partly) outside the printable area. The designer warns about these. */
export function badgeOutOfBounds(layout) {
    const { widthDots, heightDots } = badgeSurface(layout);
    return layout.elements
        .filter((el) => {
        const r = rectOf(el);
        return r.x + r.w > widthDots || r.y + r.h > heightDots;
    })
        .map((el) => el.id);
}
/**
 * Turns a layout (mm, label orientation) into draw operations in dots at 300 dpi on the printable area. Elements are
 * clipped to the printable area; elements with no data (no photo, missing logo, empty text, no QR) are dropped.
 * Text fitting is left to each renderer (font metrics differ).
 */
export function resolveBadge(layout, data) {
    const surface = badgeSurface(layout);
    const ops = [];
    for (const el of layout.elements) {
        const full = rectOf(el);
        const r = clip(full, surface.widthDots, surface.heightDots);
        if (!r)
            continue;
        switch (el.type) {
            case 'text': {
                const text = el.source.kind === 'static' ? el.source.text : (data.values[el.source.key] ?? '');
                if (text.trim() === '')
                    break;
                ops.push({ kind: 'text', ...r, text, fontSizeDots: ptToDots(el.fontSize), bold: el.bold, align: el.align, shrinkToFit: el.shrinkToFit, maxLines: el.maxLines });
                break;
            }
            case 'image': {
                const available = el.source === 'photo' ? data.hasPhoto : data.logos[el.source === 'tenant_logo' ? 'tenant' : el.source === 'site_logo' ? 'site' : 'template'];
                if (!available)
                    break;
                ops.push({ kind: 'image', ...r, slot: el.source, fit: el.fit });
                break;
            }
            case 'qr': {
                if (!data.qrPayload)
                    break;
                // A QR must stay square and whole to scan: use the largest square inside the box, clipped box permitting.
                const size = Math.min(full.w, full.h, surface.widthDots - Math.max(0, full.x), surface.heightDots - Math.max(0, full.y));
                if (size < 1)
                    break;
                ops.push({ kind: 'qr', x: Math.max(0, full.x), y: Math.max(0, full.y), size, payload: data.qrPayload });
                break;
            }
            case 'box':
                ops.push({ kind: 'box', ...r, strokeDots: Math.max(1, mmToDots(el.strokeMm)), fill: el.fill });
                break;
        }
    }
    return { ...surface, ops };
}
function fmt(iso, timeZone, opts) {
    try {
        return new Intl.DateTimeFormat('en-GB', { timeZone, ...opts }).format(new Date(iso));
    }
    catch {
        return '';
    }
}
/** Text values a badge can show for a visit. Field texts are keyed by field id (with or without `field:`). */
export function badgeDataForVisit(input) {
    const name = input.visitorName.trim();
    const out = {
        visitor_name: name,
        first_name: name.split(/\s+/)[0] ?? '',
        group_name: input.groupName,
        site_name: input.siteName,
        host_name: input.hostName ?? '',
        date: fmt(input.signedInAt, input.timeZone, { day: '2-digit', month: '2-digit', year: 'numeric' }),
        time_in: fmt(input.signedInAt, input.timeZone, { hour: '2-digit', minute: '2-digit', hour12: false }),
        tenant_name: input.tenantName,
    };
    for (const [id, text] of Object.entries(input.fieldText))
        out[id.startsWith('field:') ? id : `field:${id}`] = text;
    return out;
}
//# sourceMappingURL=resolve.js.map